import React, { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { GalleryImageSectionRef, GalleryImageSectionProps } from './GalleryImageSection.types'
// import styles from './GalleryImageSection.module.scss'
// import cl from 'classnames'
import { GallerySection } from '../GallerySection'
import { ImageEditable, ImageGalleryEditor } from 'components/shared'
import { FileStatus, parseToGalleryImage, asyncForEach, Cloud } from 'utilities'
import { GalleryImage, MediaContentDTO } from 'models'
import { useDropzone } from 'react-dropzone'

const GalleryImageSectionComponent = (
  props: GalleryImageSectionProps,
  ref: Ref<GalleryImageSectionRef>
) => {
  const {
    initialImages,
    title = 'Poster',
    onChange
  } = props

  const [images, setImages] = useState<GalleryImage[]>(
    (initialImages || []).map(parseToGalleryImage)
  )
  const [indexOnFocus, setIndexOnFocus] = useState(-1)

  useEffect(() => {
    if (!initialImages || !initialImages.length) return
    setImages(initialImages.map(parseToGalleryImage)) 
    return () =>{
      setImages([]) // for cleanup the image state
    }
  }, [initialImages])

  useImperativeHandle(ref, () => ({
    async handleImages () {
      await asyncForEach(
        [...images],
        async (img: GalleryImage, indx: number, imgs: GalleryImage[]) => {
          if (img.status !== FileStatus.LOADED) return

          imgs[indx].status = FileStatus.UPLOADING
          setImages([...imgs])

          const response = await Cloud.uploadFile(img.file)
          imgs[indx] = {
            ...img,
            src: response.location,
            status: FileStatus.UPLOADED
          }
          setImages([...imgs])
        })
    },
    getImages () {
      return images.map(({ file: _, ...img }) => ({
        ...img,
        url: img.src,
        label: ''
      } as MediaContentDTO))
    }
  })
  )

  const removeImageAtIndex = index => {
    const imagesClone = [...images]
    imagesClone.splice(index, 1)
    setImages(imagesClone)
  }

  const saveOriginalDimensions = (
    originalWidth: number, originalHeight: number, index: number
  ) => {
    const image = images[index]
    const imageAlreadyGotDimensions = image.originalHeight && image.originalWidth
    if (imageAlreadyGotDimensions) return

    const cloneImages = [...images]
    cloneImages[index] = {
      ...image,
      originalHeight,
      originalWidth
    }
    setImages(cloneImages)
  }

  const onDrop = useCallback((files: File[]) => {
    files.forEach(currentFile => {
      const fileReader = new FileReader()

      fileReader.addEventListener('load', () => {
        Object.defineProperty(currentFile, 'name', {
          writable: true,
          value: `${Number(new Date())}_${currentFile.name}`
        })
        const image: GalleryImage = {
          file: currentFile,
          src: fileReader.result,
          status: FileStatus.LOADED,
          contentType: currentFile.type
        }
        setImages(i => [...i, image])
        onChange && onChange()
      }, false)

      fileReader.readAsDataURL(currentFile)
    })
  }, [onChange])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ['image/png', 'image/jpeg']
  })
  const { onClick } = getRootProps()

  return (
    <>
      <GallerySection
        title={title}
        count={images.length}
        addButtonProps={{
          width: 161,
          label: 'Add image',
          onClick
        }}
      >
        {images.map((img, index) =>
          <ImageEditable
            key={index}
            galleryImage={img}
            openImageEditor={() => setIndexOnFocus(index)}
            removeItem={() => removeImageAtIndex(index)}
            saveOriginalDimensions={(w, h) => saveOriginalDimensions(w, h, index)}
          // image={}
          // src={src}
          // width={161}
          />
        )}
      </GallerySection>
      <input {...getInputProps()} />
      {indexOnFocus > -1 &&
        <ImageGalleryEditor
          onClose={() => setIndexOnFocus(-1)}
          images={images}
          setImages={setImages}
          indexOnFocus={indexOnFocus}
        // additionalProps={additionalProps}
        />
      }
    </>
  )
}

const GalleryImageSection = forwardRef(GalleryImageSectionComponent)
export { GalleryImageSection }
